// Components/Services/FullHomeRemodel/HomeRemodelWhoItsFor.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiHome, FiTrendingUp, FiRefreshCw, FiUsers, FiStar, FiTool } from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const audience = [
  {
    icon: <FiHome className="w-5 h-5 text-[#212529]" />,
    title: "Long-Term Homeowners",
    desc: "You've been in the home for years and it's time for a real upgrade — not just a coat of paint, but a transformation that matches how you actually live today.",
  },
  {
    icon: <FiTrendingUp className="w-5 h-5 text-[#212529]" />,
    title: "Pre-Sale Renovators",
    desc: "Sellers who want to maximize resale value with targeted renovations — kitchens, bathrooms, and curb appeal that buyers pay a premium for.",
  },
  {
    icon: <FiRefreshCw className="w-5 h-5 text-[#212529]" />,
    title: "New Home Buyers",
    desc: "You purchased a home that needs work before move-in. We gut it, rebuild it right, and hand it back to you move-in ready.",
  },
  {
    icon: <FiUsers className="w-5 h-5 text-[#212529]" />,
    title: "Growing Families",
    desc: "Need more space without moving? We handle additions, room conversions, and open-concept reconfigurations to fit your expanding household.",
  },
  {
    icon: <FiStar className="w-5 h-5 text-[#212529]" />,
    title: "Luxury Upgrade Clients",
    desc: "Homeowners in high-end markets who want premium materials, custom finishes, and a contractor who won't cut corners on the details.",
  },
  {
    icon: <FiTool className="w-5 h-5 text-[#212529]" />,
    title: "Investors & Flippers",
    desc: "Real estate investors who need reliable, fast-turnaround remodels that hold up to scrutiny — on budget and on schedule.",
  },
];

const HomeRemodelWhoItsFor = () => {
  return (
    <section className="bg-[#f8f9fa] py-16 px-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p variants={fadeUp} className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-3">
            Is This Right For You?
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-black text-[#212529] leading-tight mb-4">
            Who Full Home Remodels Are Built For
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#495057] text-base leading-relaxed max-w-2xl mx-auto">
            We work best with clients who are serious about quality, want one point of contact from start to finish, and are ready to invest in their home the right way.
          </motion.p>
        </motion.div>

        {/* 3-column grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
        >
          {audience.map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ x: 4, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="flex items-start gap-4 bg-white rounded-xl px-5 py-5 border border-[#dee2e6] shadow-sm cursor-default"
            >
              <div className="w-10 h-10 rounded-full bg-[#f8f9fa] border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                {item.icon}
              </div>
              <div>
                <p className="text-[#212529] font-bold text-sm">{item.title}</p>
                <p className="text-[#6c757d] text-xs mt-1 leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-12 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <p className="text-[#495057] text-sm mb-4">Not sure if a full remodel is right for your situation? We'll give you an honest answer in 15 minutes.</p>
          <a
            href="/contact"
            className="inline-block bg-[#212529] hover:bg-[#343a40] text-white text-sm font-semibold uppercase tracking-wider px-8 py-3 rounded-full transition duration-300"
          >
            Talk to Our Team →
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default HomeRemodelWhoItsFor;